import { useEffect, useState } from 'react';
import { withdrawalProcessAPI } from '../../lib/api';
import { receiptUrl } from './BankTransferReceiptFields';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Landmark, Loader2, Clock, Search, CheckCircle2, XCircle, Eye } from 'lucide-react';

const TABS = [
    { key: 'pending', label: 'Pendientes', Icon: Clock, cls: 'text-amber-400' },
    { key: 'in_review', label: 'En revisión', Icon: Search, cls: 'text-sky-400' },
    { key: 'verified', label: 'Verificados', Icon: CheckCircle2, cls: 'text-emerald-400' },
    { key: 'rejected', label: 'Rechazados', Icon: XCircle, cls: 'text-red-400' },
];
const fmt = (a) => `${Number(a || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

export const WithdrawalProcessAdminPanel = () => {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [tab, setTab] = useState('pending');
    const [loading, setLoading] = useState(true);
    const [action, setAction] = useState(null);
    const [reason, setReason] = useState('');
    const [busy, setBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await withdrawalProcessAPI.adminList();
            setItems(data.requests || []); setCounts(data.counts || {});
        } catch { toast.error('Error al cargar solicitudes del proceso de retiro'); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const confirm = async () => {
        if (action.status === 'rejected' && reason.trim().length < 5) { toast.error('Indica el motivo del rechazo'); return; }
        setBusy(true);
        try {
            await withdrawalProcessAPI.adminStatus(action.req.id, action.status, reason.trim());
            toast.success('Estado actualizado y usuario notificado');
            setAction(null); setReason(''); load();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error al actualizar'); }
        finally { setBusy(false); }
    };

    const visible = items.filter((r) => r.status === tab);
    const TITLES = { in_review: 'Marcar en revisión', verified: 'Marcar como verificado', rejected: 'Rechazar justificante' };

    return (
        <div className="space-y-4" data-testid="withdrawal-process-admin-panel">
            <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-[#7CB1E5]" />
                <h2 className="text-white font-semibold text-base md:text-lg">Abonar Proceso de Retiro · Justificantes al agente autorizado</h2>
            </div>
            <p className="text-xs text-slate-500">Verificar un justificante NO activa ni garantiza ningún retiro automáticamente; solo registra la revisión administrativa.</p>
            <div className="flex flex-wrap gap-2">
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        className={`flex items-center gap-2 px-3.5 py-2 rounded-xl border text-sm font-semibold transition-colors ${tab === t.key ? 'bg-slate-800 border-slate-600 text-white' : 'bg-slate-900/50 border-slate-800 text-slate-400 hover:text-white'}`}
                        data-testid={`wp-admin-tab-${t.key}`}>
                        <t.Icon className={`w-4 h-4 ${t.cls}`} /> {t.label}
                        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-slate-800 text-slate-300">{counts[t.key] || 0}</span>
                    </button>
                ))}
            </div>
            <div className="rounded-2xl bg-slate-900/70 border border-slate-800 overflow-hidden">
                {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#7CB1E5] animate-spin" /></div>
                    : visible.length === 0 ? <p className="text-slate-600 text-sm text-center py-10" data-testid="wp-admin-empty">Sin solicitudes en esta categoría.</p>
                    : (
                        <div className="divide-y divide-slate-800/60">
                            {visible.map((r) => (
                                <div key={r.id} className="px-4 py-3.5 flex flex-wrap items-center gap-3" data-testid={`wp-admin-item-${r.id}`}>
                                    <div className="flex-1 min-w-[220px]">
                                        <p className="text-white text-sm font-semibold">{fmt(r.amount)} · {r.modality_subtitle} · {r.user_name}</p>
                                        <p className="text-slate-500 text-xs">{r.user_email} · {r.filename} · {fmtDate(r.created_at)}</p>
                                        <p className="text-slate-600 text-[10.5px]">Destinatario: {r.agent?.name} · {r.agent?.bank} · {r.agent?.iban} · Condiciones aceptadas {fmtDate(r.accepted_terms_at)}</p>
                                        {r.note && <p className="text-slate-400 text-xs mt-0.5">Nota del usuario: {r.note}</p>}
                                        {r.reason && <p className="text-red-300 text-xs mt-0.5">Motivo: {r.reason}</p>}
                                        {r.reviewed_by && <p className="text-slate-600 text-[10.5px]">Cerrado por {r.reviewed_by} · {fmtDate(r.reviewed_at)}</p>}
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <a href={receiptUrl(r.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center h-8 px-3 rounded-md border border-slate-700 text-slate-300 hover:bg-slate-800 text-xs font-semibold" data-testid={`wp-admin-view-${r.id}`}>
                                            <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                                        </a>
                                        {r.status === 'pending' && (
                                            <Button size="sm" onClick={() => { setAction({ req: r, status: 'in_review' }); setReason(''); }} className="h-8 bg-sky-500/15 border border-sky-500/40 text-sky-300 hover:bg-sky-500/25" data-testid={`wp-admin-review-${r.id}`}>
                                                <Search className="w-3.5 h-3.5 mr-1" /> En revisión
                                            </Button>
                                        )}
                                        {(r.status === 'pending' || r.status === 'in_review') && (
                                            <>
                                                <Button size="sm" onClick={() => { setAction({ req: r, status: 'verified' }); setReason(''); }} className="h-8 bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/25" data-testid={`wp-admin-verify-${r.id}`}>
                                                    <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Verificar
                                                </Button>
                                                <Button size="sm" onClick={() => { setAction({ req: r, status: 'rejected' }); setReason(''); }} className="h-8 bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25" data-testid={`wp-admin-reject-${r.id}`}>
                                                    <XCircle className="w-3.5 h-3.5 mr-1" /> Rechazar
                                                </Button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
            </div>

            <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
                <DialogContent className="max-w-md bg-slate-900 border-slate-700 text-white" data-testid="wp-admin-dialog">
                    <DialogHeader><DialogTitle className="text-white">{action && TITLES[action.status]}</DialogTitle></DialogHeader>
                    <div className="space-y-3 py-1">
                        <p className="text-slate-400 text-sm">{action?.req?.user_name} · <span className="text-white font-semibold">{action && fmt(action.req.amount)}</span> · {action?.req?.modality_subtitle}</p>
                        {action?.status === 'verified' && <p className="text-xs text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2">La verificación solo registra la revisión administrativa del justificante. No activa ni garantiza ningún retiro.</p>}
                        {action?.status === 'rejected' && <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo del rechazo (obligatorio)" className="bg-slate-950 border-slate-800 text-white text-sm min-h-[90px]" data-testid="wp-admin-reason-input" />}
                        {action?.status === 'in_review' && <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Nota interna (opcional)" className="bg-slate-950 border-slate-800 text-white text-sm min-h-[70px]" data-testid="wp-admin-note-input" />}
                    </div>
                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setAction(null)} className="border-slate-700 text-slate-300 hover:bg-slate-800">Cancelar</Button>
                        <Button onClick={confirm} disabled={busy} className="bg-[#1973B8] hover:bg-[#1973B8]/85 text-white" data-testid="wp-admin-confirm-btn">
                            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1.5" />} Confirmar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
